/**
 * Bought ranks: the wolves-into-dire-wolves axis.
 *
 * There are two kinds of "better troops" in this game and they are deliberately
 * different things.
 *
 *   *Veterancy* is earned. A company that fights gains XP and climbs Raw →
 *   Blooded → Veteran → Ironbound → Storied, and it dies with the men who
 *   earned it. That is `RANK_XP` in `systems/companies.ts` and it is untouched
 *   by any of this.
 *
 *   A *bought rank* is bought, permanent, and belongs to the unit *type* rather
 *   than to any company. Buy Militia II and every militia you have ever raised
 *   and ever will raise is a Militia II. It is the settlement getting better at
 *   making soldiers, not the soldiers getting better at soldiering.
 *
 * Three ranks per type, each gated behind an Age, which is what ties the war
 * roster to the climb: an Age is the permission to improve what you already
 * field, as well as the door to what you cannot yet.
 *
 * A rank-III Age-1 militia lands somewhere near a stock Age-2 unit. That is
 * deliberate — investment in an old line should be outpaced, never wasted.
 */

import type { ResourceBundle } from '../sim/types.ts';
import { MAX_AGE } from './ages.ts';
import type { UnitType } from './units.ts';

/** How many bought ranks a unit type can have. */
export const MAX_UNIT_RANK = 3;

/** Health multiplier per bought rank, compounding. */
const HP_PER_RANK = 1.3;
/** Damage multiplier per bought rank, compounding. */
const DMG_PER_RANK = 1.2;

/**
 * The Age you must have reached to buy each rank.
 *
 * Indexed by the rank being bought, so `[1]` is the first upgrade. Rank I is
 * available from the Age a unit itself unlocks in; II and III need one and two
 * Ages beyond that.
 */
const RANK_AGE_STEP = [0, 0, 1, 2];

/** Health multiplier for a unit type at `rank` bought ranks. */
export function rankHpMul(rank: number): number {
  return Math.pow(HP_PER_RANK, clampRank(rank));
}

/** Damage multiplier for a unit type at `rank` bought ranks. */
export function rankDamageMul(rank: number): number {
  return Math.pow(DMG_PER_RANK, clampRank(rank));
}

function clampRank(rank: number): number {
  return Math.max(0, Math.min(MAX_UNIT_RANK, Math.floor(rank)));
}

/**
 * The Age required to buy `rank` for a unit of tier `tier`.
 *
 * Clamped to the top of the ladder, and it has to be. The step table asks for
 * one Age past the unit's own for rank II and two for rank III, which is the
 * right shape in the middle of the ladder and walks straight off the end of it
 * at the top: a Hollowed is tier 5, so its rank III wanted Age 7, and the whole
 * of the Gilded and Hollow rosters had ranks that could never be bought by
 * anybody, in any run, ever. A dead entry in a menu is worse than an expensive
 * one.
 *
 * So the last Age unlocks everything the last Age can hold, and the gate on a
 * Knell Engine III stops being an Age and becomes its price — which is a little
 * over five hundred hollowglass and a six-figure sum in coin, because `rankCost`
 * scales on threat and a Knell Engine's threat is 250. That is a grind rather
 * than a gate, and a grind is what the top of this ladder is for.
 */
export function rankAgeRequired(t: UnitType, rank: number): number {
  return Math.min(MAX_AGE, t.tier + (RANK_AGE_STEP[clampRank(rank)] ?? 0));
}

/**
 * What the next rank costs.
 *
 * Scales on the unit's own threat — upgrading a line of heavy cavalry is
 * supposed to hurt more than upgrading spearmen — and steeply on the rank, so
 * the third is a genuine commitment rather than a formality.
 *
 * Priced in the goods of **the Age that permits it**, which is the whole reason
 * the Age gate above and this table sit in the same file — and it is the gate
 * that decides, not the rank number. A militia's rank II is an Age-2 purchase
 * and is paid in Kindling goods; a Voidsworn's rank II is an Age-4 purchase and
 * is paid in the Gilded's. That is the second axis of the design doing its job:
 * the recipe chain is what makes the soldiers you already have better, and not
 * only the ones you cannot yet raise.
 *
 * The consequence worth stating: a rank-III militia costs Bell goods, so the
 * Age-1 line keeps a claim on the top of the chain for the whole run. It is
 * outpaced and never wasted, which was the promise.
 *
 * Gold rides along from the second Age up, because by then the standing wage
 * bill, not the yard, is what is actually stopping you. Souls join it at the
 * Hollow, where nothing is bought without them.
 */
export function rankCost(t: UnitType, rank: number): Partial<ResourceBundle> {
  const r = clampRank(rank);
  if (r <= 0) return {};
  const scale = Math.max(1, t.threat) * Math.pow(2.4, r - 1);
  const gold = Math.round(30 * scale);
  switch (Math.min(5, rankAgeRequired(t, r))) {
    case 1:
      return { timber: Math.round(18 * scale), iron: Math.round(14 * scale) };
    case 2:
      return { magewood: Math.round(3.2 * scale), ingots: Math.round(2.6 * scale), gold };
    case 3:
      return {
        soulwood: Math.round(1.5 * scale),
        alloy: Math.round(1.3 * scale),
        runestone: Math.round(2.2 * scale),
        gold,
      };
    case 4:
      return { ashglass: Math.round(0.7 * scale), heartiron: Math.round(0.6 * scale), gold: gold * 2 };
    default:
      return {
        hollowglass: Math.round(0.35 * scale),
        gravesilver: Math.round(0.3 * scale),
        souls: Math.round(6 * scale),
        gold: gold * 3,
      };
  }
}

/** 'I', 'II', 'III' — how a bought rank is written on a card. */
export const RANK_NUMERAL = ['', 'I', 'II', 'III'];
